import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { householdAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useIsMobile } from '../hooks';
import { 
  Home, Users, CreditCard, Settings as SettingsIcon, PlusCircle, UserPlus, Tag,
  ChevronRight
} from 'lucide-react';

// Import extracted components
import { 
  HouseholdOverview, 
  HouseholdMembers, 
  PaymentAccounts, 
  BillCategories,
  HouseholdSettings
} from '../components/household';
import CreateHouseholdWizard from '../components/CreateHouseholdWizard';
import BrowseHouseholdsModal from '../components/BrowseHouseholdsModal';

import '../styles/Household.css';
import '../styles/ModalTabs.css';

function Household({ user }) {
  const { success, error: showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  
  const isMobile = useIsMobile(768);
  // On mobile, start with menu open only if no tab is specified in URL
  const [mobileMenuOpen, setMobileMenuOpen] = useState(() => {
    const urlTab = searchParams.get('tab');
    return !urlTab;
  });
  
  const isHouseholdManager = user?.household_role === 'manager';

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) setActiveTab(tabFromUrl);
  }, [searchParams]);

  useEffect(() => {
    loadHousehold();
  }, []);

  const loadHousehold = async () => {
    setLoading(true);
    try {
      const response = await householdAPI.getCurrent();
      setHousehold(response.data);
    } catch (error) {
      setHousehold(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHousehold = async (householdData) => {
    try {
      // Create the household
      await householdAPI.create({
        name: householdData.householdName,
        settings: {}
      });

      // Send invitations to selected users
      if (householdData.invitations && householdData.invitations.length > 0) {
        for (const user of householdData.invitations) {
          try {
            await householdAPI.inviteUser(user.id, 'member');
          } catch (inviteError) {
            console.error(`Failed to invite user ${user.email}:`, inviteError);
          }
        }
      }

      success('Household created successfully!');
      setShowCreateWizard(false);
      window.location.reload();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to create household');
      throw error;
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    if (isMobile) {
      setMobileMenuOpen(false); // Hide menu, show content
    }
    document.querySelector('.sidebar-layout__content')?.scrollTo(0, 0);
  };

  const handleMobileBack = () => {
    setMobileMenuOpen(true); // Show menu again
    // Clear the tab from URL so refresh stays on menu
    setSearchParams({}, { replace: true });
  };

  if (loading) {
    return (
      <div className="page household-page">
        <div className="loading-state">Loading household information...</div>
      </div>
    );
  }

  if (!household) {
    return (
      <NoHouseholdView 
        user={user}
        onCreateHousehold={() => setShowCreateWizard(true)}
        onJoinHousehold={() => setShowJoinModal(true)}
        showCreateWizard={showCreateWizard}
        setShowCreateWizard={setShowCreateWizard}
        showJoinModal={showJoinModal}
        setShowJoinModal={setShowJoinModal}
        handleCreateHousehold={handleCreateHousehold}
        success={success}
      />
    );
  }

  const memberTabs = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'accounts', label: 'Payment Accounts', icon: CreditCard },
    { id: 'categories', label: 'Bill Categories', icon: Tag },
  ];

  const managerTabs = [
    { id: 'settings', label: 'Household Settings', icon: SettingsIcon },
  ];

  const tabs = isHouseholdManager ? [...memberTabs, ...managerTabs] : memberTabs;

  // Render the tab content (shared between mobile and desktop)
  const renderContent = () => (
    <>
      {activeTab === 'overview' && <HouseholdOverview household={household} />}
      {activeTab === 'members' && <HouseholdMembers isManager={isHouseholdManager} />}
      {activeTab === 'accounts' && <PaymentAccounts isManager={isHouseholdManager} isInHousehold={true} />}
      {activeTab === 'categories' && <BillCategories isManager={isHouseholdManager} isInHousehold={true} />}
      {activeTab === 'settings' && isHouseholdManager && <HouseholdSettings />}
    </>
  );

  return (
    <div className="page household-page">
      <div className={`sidebar-layout ${isMobile ? 'sidebar-layout--mobile-menu' : ''}`}>
        {isMobile ? (
          /* Mobile: Full Page List Pattern */
          mobileMenuOpen ? (
            /* Menu List */
            <div className="sidebar-layout__mobile-menu">
              <div className="sidebar-layout__mobile-header">
                <h2>Household</h2>
              </div>
              <div className="sidebar-layout__mobile-list">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      className={`sidebar-layout__mobile-item ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <Icon size={20} />
                      <span>{tab.label}</span>
                      <ChevronRight size={18} className="chevron" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Content View */
            <div className="sidebar-layout__mobile-content">
              <button className="sidebar-layout__mobile-back" onClick={handleMobileBack}>
                <ChevronRight size={16} className="back-icon" />
                <span>Household</span>
              </button>
              <div className="sidebar-layout__content household-content">
                {renderContent()}
              </div>
            </div>
          )
        ) : (
          /* Desktop Layout */
          <>
            <div className="sidebar-layout__nav">
              <div className="sidebar-layout__nav-inner">
                <nav className="nav-tabs nav-tabs--vertical nav-tabs--household">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                      >
                        <Icon size={20} />
                        <span className="nav-tab__label">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            <div className="sidebar-layout__content household-content">
              {renderContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// No Household View - For users without a household
function NoHouseholdView({ 
  user,
  onCreateHousehold, 
  onJoinHousehold,
  showCreateWizard,
  setShowCreateWizard,
  showJoinModal,
  setShowJoinModal,
  handleCreateHousehold,
  success
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('welcome');
  const isMobile = useIsMobile(768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(() => {
    const urlTab = searchParams.get('tab');
    return !urlTab;
  });

  const privateTabs = [
    { id: 'welcome', label: 'Welcome', icon: Home },
    { id: 'accounts', label: 'Payment Accounts', icon: CreditCard },
    { id: 'categories', label: 'Bill Categories', icon: Tag },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId }, { replace: true });
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleMobileBack = () => {
    setMobileMenuOpen(true);
    setSearchParams({}, { replace: true });
  };

  // Render the tab content
  const renderContent = () => (
    <>
      {activeTab === 'welcome' && (
        <PrivateModeWelcome 
          onCreateHousehold={onCreateHousehold} 
          onJoinHousehold={onJoinHousehold} 
        />
      )}
      {activeTab === 'accounts' && <PaymentAccounts isManager={true} isPrivateMode={true} />}
      {activeTab === 'categories' && <BillCategories isManager={true} isPrivateMode={true} />}
    </>
  );

  return (
    <>
      <div className="page household-page">
        <div className={`sidebar-layout ${isMobile ? 'sidebar-layout--mobile-menu' : ''}`}>
          {isMobile ? (
            /* Mobile: Full Page List Pattern */
            mobileMenuOpen ? (
              /* Menu List */
              <div className="sidebar-layout__mobile-menu">
                <div className="sidebar-layout__mobile-header">
                  <h2>Private Mode</h2>
                </div>
                <div className="sidebar-layout__mobile-list">
                  {privateTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        className={`sidebar-layout__mobile-item ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                      >
                        <Icon size={20} />
                        <span>{tab.label}</span>
                        <ChevronRight size={18} className="chevron" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Content View */
              <div className="sidebar-layout__mobile-content">
                <button className="sidebar-layout__mobile-back" onClick={handleMobileBack}>
                  <ChevronRight size={16} className="back-icon" />
                  <span>Private Mode</span>
                </button>
                <div className="sidebar-layout__content household-content">
                  {renderContent()}
                </div>
              </div>
            )
          ) : (
            /* Desktop Layout */
            <>
              <div className="sidebar-layout__nav">
                <div className="sidebar-layout__nav-inner">
                  <nav className="nav-tabs nav-tabs--vertical nav-tabs--household">
                    {privateTabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={() => handleTabChange(tab.id)}
                        >
                          <Icon size={20} />
                          <span className="nav-tab__label">{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>

              <div className="sidebar-layout__content household-content">
                {renderContent()}
              </div>
            </>
          )}
        </div>
      </div>
      
      <CreateHouseholdWizard
        isOpen={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        onComplete={handleCreateHousehold}
        currentUser={user}
      />

      <BrowseHouseholdsModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onRequestSent={() => success('Join request sent successfully!')}
      />
    </>
  );
}

// Private Mode Welcome Tab
function PrivateModeWelcome({ onCreateHousehold, onJoinHousehold }) {
  return (
    <div className="household-section">
      <h2>Private Mode</h2>
      <p className="section-description">You're using Remidy in private mode</p>
      
      <div className="private-welcome-content">
        <div className="welcome-section">
          <h3>💡 What is Private Mode?</h3>
          <p style={{ marginBottom: 'var(--spacing-md)' }}>
            Private Mode allows you to use Remidy for personal finance management. Your data is completely private to you.
          </p>
          <ul className="feature-list">
            <li><strong>Payment Accounts:</strong> Manage your bank accounts, credit cards, and cash</li>
            <li><strong>Bill Categories:</strong> Create custom categories to organize your bills</li>
            <li><strong>Personal Settings:</strong> Customize your preferences and appearance</li>
            <li><strong>Full Privacy:</strong> Your information stays completely private</li>
          </ul>
        </div>

        <div className="welcome-section">
          <div className="welcome-section-header">
            <h3>👥 Household Features</h3>
            <div className="welcome-section-actions">
              <button className="btn-primary" onClick={onCreateHousehold}>
                <PlusCircle size={18} />
                Create Household
              </button>
              <button className="btn-primary" onClick={onJoinHousehold}>
                <UserPlus size={18} />
                Join Household
              </button>
            </div>
          </div>
          <p style={{ marginBottom: 'var(--spacing-md)' }}>
            If you want to share bills and expenses with others, you can create or join a household:
          </p>
          <ul className="feature-list">
            <li><strong>Shared Bills:</strong> Track bills and expenses with family or roommates</li>
            <li><strong>Payment Tracking:</strong> See who paid what and when</li>
            <li><strong>Reminders:</strong> Get notifications for upcoming bills</li>
            <li><strong>Collaboration:</strong> Multiple people can manage finances together</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Household;
